import { useCallback, useMemo } from 'react';
import { LOCAL_STORAGE_SCHEMA_VERSION } from '../lib/autosave-config';
import { draftStorageKey } from '../lib/draft-storage-keys';
import type { DraftPatch } from '../types/draft';

interface MirrorEntry {
  draft: DraftPatch & { id: string | null };
  lastEditedAt: string;
  schemaVersion: number;
}

export interface LocalDraftMirror {
  read: () => MirrorEntry | null;
  write: (patch: DraftPatch) => void;
  clear: () => void;
}

export function useLocalDraftMirror(id: string | null): LocalDraftMirror {
  const key = useMemo(() => draftStorageKey(id), [id]);

  const read = useCallback((): MirrorEntry | null => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as MirrorEntry;
      if (parsed.schemaVersion !== LOCAL_STORAGE_SCHEMA_VERSION) return null;
      return parsed;
    } catch {
      return null;
    }
  }, [key]);

  const write = useCallback(
    (patch: DraftPatch) => {
      const entry: MirrorEntry = {
        draft: { ...patch, id },
        lastEditedAt: new Date().toISOString(),
        schemaVersion: LOCAL_STORAGE_SCHEMA_VERSION,
      };
      try {
        localStorage.setItem(key, JSON.stringify(entry));
      } catch {
        // Quota or disabled; silently skip. Autosave still works server-side.
      }
    },
    [id, key],
  );

  const clear = useCallback(() => {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }, [key]);

  // Memoize the wrapper so the returned reference is stable across renders.
  // Consumers (e.g. useDraftBoot) put `mirror` in a useEffect dep array; a
  // fresh object every render would re-fire the effect → setState → re-render
  // → fresh object → loop, surfacing as React error #185.
  return useMemo(() => ({ read, write, clear }), [read, write, clear]);
}
